import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Loader2, Target, Radar, Users,
} from 'lucide-react';
import ScamIQBadge from '../components/ScamIQBadge';
import PremiumScenarioCard from '../components/PremiumScenarioCard';
import { API_URL } from '../config';
import '../styles/ScamSmart.css';

const API = `${API_URL}/api`;
const SHIELD_LOGO = 'https://customer-assets.emergentagent.com/job_express-checkout-9/artifacts/gw8x19qe_Variant%201_Shield%20Only_Vector%20copy.png';

const CATEGORY_LABEL = {
  core: 'Core scam patterns',
  family_emergency: 'Family emergency / new number',
  banking_government: 'Banking & government impersonation',
  delivery_shopping: 'Delivery & online shopping',
  marketplace_payid: 'Marketplace / PayID',
  account_security: 'Account security',
  emerging: 'Emerging patterns',
};
const rankFor = (s) => {
  if (s >= 90) return 'Human Firewall';
  if (s >= 75) return 'Hard to Fool';
  if (s >= 60) return 'Street Smart';
  if (s >= 40) return 'Getting Sharper';
  return 'Needs Practice';
};

const ScamSmartTest = ({ ssEmail }) => {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [results, setResults] = useState([]); // [{scenario, result, points, ruleUnlocked}]
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/scamsmart/v2/scenarios/all`, { params: { email: ssEmail } })
      .then(({ data }) => setScenarios(data.scenarios || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ssEmail]);

  const current = scenarios[idx];

  const handleComplete = (r) => {
    const newResults = [...results, r];
    setResults(newResults);
    if (idx + 1 < scenarios.length) {
      setIdx(idx + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const metrics = useMemo(() => {
    if (!results.length) return null;
    const total = results.reduce((s, r) => s + (r.points || 0), 0);
    const max = results.length * 10;
    const score = Math.round((total / max) * 100);
    const safe = results.filter((r) => r.result === 'safe').length;
    const risky = results.filter((r) => r.result === 'risky').length;
    const dangerous = results.filter((r) => r.result === 'dangerous').length;
    return { score, rank: rankFor(score), safe, risky, dangerous };
  }, [results]);

  useEffect(() => {
    if (done && metrics) {
      try { localStorage.setItem('ss_latest_result', JSON.stringify({ ...metrics, at: new Date().toISOString() })); } catch {}
    }
  }, [done, metrics]);

  if (loading) {
    return (
      <div className="ss-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
        <Loader2 size={36} style={{ animation: 'ss-spin 1s linear infinite', color: '#16B1C4' }} />
        <style>{`@keyframes ss-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div className="ss-page">
      <header className="ss-header">
        <div className="ss-header-inner">
          <Link to="/scamsmart/dashboard" className="ss-brand">
            <img src={SHIELD_LOGO} alt="" className="ss-brand-mark" />
            <div>
              <div className="ss-brand-text">All Secured</div>
              <div className="ss-brand-sub">Scam IQ Test</div>
            </div>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ScamIQBadge />
            <Link to="/scamsmart/dashboard" className="ss-header-cta">Dashboard</Link>
          </div>
        </div>
      </header>

      <div className="ss-test-wrap">
        <div className="ss-test-container">
          {!done && current && (
            <>
              <Link to="/scamsmart/dashboard" className="ss-back"><ArrowLeft size={14} /> Dashboard</Link>
              <div className="ss-progress">
                <span>Scenario {idx + 1} of {scenarios.length}</span>
                <span>Full Scam IQ Test</span>
              </div>
              <div className="ss-progress-bar">
                <div className="ss-progress-bar-fill" style={{ width: `${(idx / scenarios.length) * 100}%` }} />
              </div>
              <PremiumScenarioCard
                key={current.id}
                scenario={current}
                onComplete={handleComplete}
              />
            </>
          )}

          {done && metrics && (
            <Report metrics={metrics} results={results} navigate={navigate} />
          )}
        </div>
      </div>
    </div>
  );
};

const Report = ({ metrics, results, navigate }) => {
  const catStats = {};
  for (const r of results) {
    const c = r.scenario.category;
    if (!catStats[c]) catStats[c] = { safe: 0, total: 0 };
    catStats[c].total += 1;
    if (r.result === 'safe') catStats[c].safe += 1;
  }
  const ranked = Object.entries(catStats).map(([c, v]) => ({ c, ratio: v.safe / v.total, ...v })).sort((a, b) => b.ratio - a.ratio);
  const strong = ranked[0]?.c || 'core';
  const weak = ranked[ranked.length - 1]?.c || 'core';

  const supportiveMsg = metrics.score >= 75
    ? `Strong result. You made several safe choices, especially around ${CATEGORY_LABEL[strong]?.toLowerCase() || 'core scam patterns'}. Keep the pause-and-verify habit going.`
    : `Your Scam IQ is ${metrics.score}. You made ${metrics.safe} safe ${metrics.safe === 1 ? 'choice' : 'choices'}. Your main weak spot is ${CATEGORY_LABEL[weak]?.toLowerCase() || 'urgent messages'} — exactly the kind ScamSmart can help you practise.`;

  return (
    <div data-testid="ss-test-report">
      <Link to="/scamsmart/dashboard" className="ss-back"><ArrowLeft size={14} /> Dashboard</Link>
      <div className="ss-report">
        <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
          <div>
            <div className="ss-hero-iq">Your Scam IQ</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span className="ss-report-iq">{metrics.score}</span>
              <span className="ss-preview-iq-suffix">/ 100</span>
            </div>
            <div className="ss-report-rank">{metrics.rank}</div>
          </div>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 15, lineHeight: 1.65, margin: '18px 0 0' }}>
          {supportiveMsg}
        </p>
        <div className="ss-report-grid">
          <div className="ss-report-cell"><div className="label">Safe choices</div><div className="val">{metrics.safe} / {results.length}</div></div>
          <div className="ss-report-cell"><div className="label">High-risk moves</div><div className="val">{metrics.dangerous}</div></div>
          <div className="ss-report-cell"><div className="label">Strongest category</div><div className="val">{CATEGORY_LABEL[strong] || strong}</div></div>
          <div className="ss-report-cell"><div className="label">Weak spot</div><div className="val">{CATEGORY_LABEL[weak] || weak}</div></div>
        </div>
        <div className="ss-report-cta-row">
          <button className="ss-btn-primary" onClick={() => navigate('/scamsmart/radar')} data-testid="ss-report-radar-btn">
            <Radar size={16} /> Try Scam Radar
          </button>
          <button className="ss-btn-ghost" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)' }} onClick={() => navigate('/scamsmart/safe-word')}>
            <Users size={16} /> Set up Family Safe Word
          </button>
        </div>
      </div>

      <div className="ss-section" style={{ padding: '32px 0 0' }}>
        <h2 className="ss-section-title" style={{ fontSize: 22 }}>Train your weak spot</h2>
        <p className="ss-section-lede" style={{ fontSize: 15 }}>
          Open the Scenario Library to filter the premium scenarios by category and difficulty —
          and re-take this test any time to track your progress.
        </p>
        <div className="ss-cta-row">
          <button className="ss-btn-primary" onClick={() => window.location.reload()} data-testid="ss-report-retake-btn">
            <Target size={16} /> Re-take the Scam IQ Test
          </button>
          <Link to="/scamsmart/library" className="ss-btn-ghost">Open Scenario Library</Link>
        </div>
      </div>
    </div>
  );
};

export default ScamSmartTest;
